/**
 * Authentication Hook
 *
 * Manages authentication state, token persistence, and OAuth flow
 */

import { useState, useEffect, useCallback } from 'react';
import type { User, AuthTokens } from '../types';
import { parseJwt, validateToken } from '../lib/auth';
import CONFIG from '../config';

export function useAuth() {
  const [tokens, setTokens] = useState<AuthTokens>({
    idToken: null,
    accessToken: null,
    refreshToken: null,
  });
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  /**
   * Load tokens from localStorage
   */
  const loadTokens = useCallback((): AuthTokens => {
    try {
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('access_token');
      const refreshToken = localStorage.getItem('refresh_token');

      return {
        idToken,
        accessToken,
        refreshToken,
      };
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.error('Error loading tokens:', error);
      }
      return {
        idToken: null,
        accessToken: null,
        refreshToken: null,
      };
    }
  }, []);

  /**
   * Save tokens to localStorage
   */
  const saveTokens = useCallback((newTokens: AuthTokens) => {
    try {
      if (newTokens.idToken)
        localStorage.setItem('id_token', newTokens.idToken);
      if (newTokens.accessToken)
        localStorage.setItem('access_token', newTokens.accessToken);
      if (newTokens.refreshToken)
        localStorage.setItem('refresh_token', newTokens.refreshToken);
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.error('Error saving tokens:', error);
      }
    }
  }, []);

  /**
   * Clear tokens from localStorage
   */
  const clearTokens = useCallback(() => {
    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setTokens({
      idToken: null,
      accessToken: null,
      refreshToken: null,
    });
    setUser(null);
  }, []);

  /**
   * Handle OAuth callback - parse tokens from URL hash
   */
  const handleCallback = useCallback(() => {
    try {
      // Parse tokens from URL hash
      const hash = window.location.hash.substring(1);
      const params = new URLSearchParams(hash);

      const idToken = params.get('id_token');
      const accessToken = params.get('access_token');

      if (idToken) {
        const parsedUser = parseJwt(idToken);
        const newTokens: AuthTokens = {
          idToken,
          accessToken,
          refreshToken: null,
        };

        setTokens(newTokens);
        setUser(parsedUser);
        saveTokens(newTokens);

        if (CONFIG.DEBUG) {
          console.log('✅ OAuth callback successful:', parsedUser);
        }

        // Clean up URL and redirect to main page
        window.history.replaceState(null, '', '/');
        return true;
      }

      return false;
    } catch (error) {
      if (CONFIG.DEBUG) {
        console.error('Error handling OAuth callback:', error);
      }
      return false;
    }
  }, [saveTokens]);

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = useCallback((): boolean => {
    // DEV_MODE bypass
    if (CONFIG.DEV_MODE) {
      return true;
    }

    return validateToken(tokens.idToken, user);
  }, [tokens.idToken, user]);

  /**
   * Get Authorization header for API requests
   */
  const getAuthHeader = useCallback((): string | null => {
    // No auth header in dev mode
    if (CONFIG.DEV_MODE) {
      return null;
    }

    if (!tokens.idToken) {
      return null;
    }

    return `Bearer ${tokens.idToken}`;
  }, [tokens.idToken]);

  /**
   * Clear authentication error
   */
  const clearAuthError = useCallback(() => {
    setAuthError(null);
  }, []);

  /**
   * Initiate OAuth sign-in flow
   */
  const signIn = useCallback(() => {
    // Clear any previous error
    setAuthError(null);

    if (CONFIG.COGNITO_DOMAIN === 'YOUR_COGNITO_DOMAIN_HERE') {
      setAuthError(
        'Authentication is not configured. Please contact your administrator.',
      );
      return;
    }

    const authUrl =
      `https://${CONFIG.COGNITO_DOMAIN}/oauth2/authorize?` +
      `client_id=${CONFIG.COGNITO_CLIENT_ID}&` +
      `response_type=token&` +
      `scope=openid+email+profile&` +
      `redirect_uri=${encodeURIComponent(CONFIG.COGNITO_REDIRECT_URI)}&` +
      `identity_provider=Google`;

    if (CONFIG.DEBUG) {
      console.log('🔐 Redirecting to Google sign-in...');
    }

    window.location.href = authUrl;
  }, []);

  /**
   * Sign out user
   */
  const signOut = useCallback(() => {
    clearTokens();

    if (CONFIG.COGNITO_DOMAIN !== 'YOUR_COGNITO_DOMAIN_HERE') {
      const logoutUrl =
        `https://${CONFIG.COGNITO_DOMAIN}/logout?` +
        `client_id=${CONFIG.COGNITO_CLIENT_ID}&` +
        `logout_uri=${encodeURIComponent(CONFIG.COGNITO_LOGOUT_URI)}`;

      if (CONFIG.DEBUG) {
        console.log('👋 Signing out via Cognito...');
      }

      window.location.href = logoutUrl;
    } else {
      window.location.reload();
    }
  }, [clearTokens]);

  /**
   * Initialize authentication on mount
   */
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      // Check if we're handling OAuth callback
      if (window.location.hash.includes('id_token')) {
        const success = handleCallback();
        if (success) {
          setIsLoading(false);
          return;
        }
      }

      // Load tokens from storage
      const storedTokens = loadTokens();
      setTokens(storedTokens);

      // Parse user from token
      if (storedTokens.idToken) {
        const parsedUser = parseJwt(storedTokens.idToken);
        setUser(parsedUser);

        // Validate token
        if (!validateToken(storedTokens.idToken, parsedUser)) {
          clearTokens();
        }
      }

      setIsLoading(false);
    };

    init();
  }, [handleCallback, loadTokens, clearTokens]);

  return {
    user: CONFIG.DEV_MODE ? { email: 'dev@northbuilt.com' } : user,
    tokens,
    isAuthenticated: isAuthenticated(),
    isLoading,
    authError,
    signIn,
    signOut,
    getAuthHeader,
    clearAuthError,
  };
}
